import { Box, Container, type Theme } from "@mui/material";
import ProgressBar from "src/features/ProgressBar";
import { FilledButton } from "./FilledButton";

/**
 * Component used for most of the web pages for teams feature. It splits the screen
 * into two parts on web, and keeps the right part on mobile applications. Most of
 * the screens have image on the left, logo on top, and button on the buttom.
 * Those individual components are defined in the routes.
 *
 * It also has an ability to have a progress bar on top. If all three parameters
 * (showProgressBar, currentStep, totalSteps) are provided, the progress bar will be
 * shown.
 */
interface BasicLayoutProps {
  imageUrl: string | null;
  isDesktop: boolean;
  showProgressBar?: boolean;
  currentStep?: number;
  totalSteps?: number;
  buttonText: string;
  enableButton: boolean;
  isButtonVisible: boolean;
  onNextPage: () => void;
  theme: Theme;
  children: React.ReactNode;
}

function BasicLayout({
  imageUrl,
  isDesktop,
  showProgressBar,
  currentStep,
  totalSteps,
  enableButton,
  isButtonVisible,
  buttonText,
  onNextPage,
  theme,
  children,
}: BasicLayoutProps) {
  return (
    <Container
      style={{
        position: "fixed",
        right: 0,
        left: 0,
        top: 0,
        bottom: 0,
        display: "flex",
        //  Sets the container's width to 100% of its parent.
        width: "100%",
        // Ensures the container is at least the height of the viewport but can grow if needed.
        // Distributes space between child elements.
        justifyContent: "space-between",
        // Aligns child elements to center of the container's cross axis.
        alignItems: "stretch",
        // Prevents the container from shrinking.
        flexShrink: 0,
        // Stretches the container to fill the parent's cross axis.
        alignSelf: "stretch",
        // Adds a gap between child elements.
        gap: theme.spacing(2),
        // Adds padding inside the container.
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        // Ensures padding is included in the element's total height and width.
        boxSizing: "border-box",
        // Prevent scrolling.
        overflow: "hidden",
        flexDirection: "row",
      }}
    >
      {/* Container showing the left side of the screen with an image, if the page is viewed on Desktop. */}
      {isDesktop && imageUrl && (
        <Box
          component="img"
          src={imageUrl}
          sx={{
            justifyContent: "center",
            alignItems: "center",
            // Allow the image to take its natural size.
            flex: "0 1 auto",
            maxHeight: "100%",
            // Set a maximum width for the image container.
            maxWidth: "50%",
            borderRadius: theme.spacing(2),
            objectFit: "cover",
          }}
        />
      )}
      {/**
       * Container showing the right side of the screen. It contains logo, main contents, and a button at
       * at the bottom. The contents depend on the layout route.
       */}
      <Box
        sx={{
          // Makes the container a flexbox container.
          display: "flex",
          // Orients the children in the column.
          flexDirection: "column",
          // Aligns the items in the center.
          alignItems: "center",
          flex: "1 0 0",
          // Ensures the container is at least the height of the viewport but can grow if needed.
          //minHeight: "100%",
          maxHeight: "100%",
          // Add flexGrow to make the container take up all available space.
          flexGrow: 1,
        }}
      >
        <Box
          component="img"
          src="/assets/trainwell-teams-logo.png"
          alt="logo"
          // Automatically scale height.
          width={200}
          sx={{
            marginLeft: theme.spacing(2),
            marginRight: theme.spacing(2),
            marginBottom: theme.spacing(2),
          }}
        />
        {showProgressBar && totalSteps && currentStep && (
          <ProgressBar
            steps={totalSteps}
            currentStep={currentStep}
            onChangeStep={() => undefined}
          />
        )}
        <Box
          sx={{
            width: "100%",
            overflow: "auto",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {children}
        </Box>
        {isButtonVisible && (
          <FilledButton
            disabled={!enableButton}
            style={{ display: isButtonVisible ? "block" : "none" }}
            onClick={onNextPage}
            sx={{
              marginLeft: theme.spacing(2),
              marginRight: theme.spacing(2),
              width: "100%",
              display: "flex",
              maxWidth: { xs: "420px", md: imageUrl ? "420px" : "720px" },
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {buttonText}
          </FilledButton>
        )}
      </Box>
    </Container>
  );
}

export default BasicLayout;
